/**
 * nav_region1 v2 (6) — 첫 방문 지역 제안 「{지역} 지역으로 볼까요? [보기] [전국]」 의 순수 규칙(클라이언트 전용 · 테스트 대상).
 *
 *   · 서버 HTML·첫 수화 렌더에는 제안이 «없다». 수화 뒤에만 쿠키(kd_geo)·기억(kd_nav_region_v1)·숨김 기록을 읽는다.
 *   · 노출 조건(전부 참일 때 1회): 기억 없음 · 지금 화면이 지역을 주지 않음(경로 허브·?r= 아님) · kd_geo 가 표의 시·도로 풀림
 *     · 숨김 기록이 만료됨.
 *   · 숨김: [전국](닫기) = 7일. 노출만 하고 아무것도 안 누른 채 떠나면 1일(«1회 노출» — 같은 날 쪽마다 다시 뜨지 않게).
 *     [보기] = 기억이 생기므로 다시 뜨지 않는다.
 *   · Redis·DB·서버 호출 없음(계측 conversion_events 한 줄 제외).
 *
 * ⛔ 지역 이름·코드 하드코딩 없음 — ISO → 한글 시·도 이름은 geo-cookie(=region-detection 표), 이름 → 노드는 regions 표(HeaderRegionData).
 */
import type { HeaderRegionData } from '@/lib/v3/header-region-match';
import { findScopeNode, type ScopeNode } from '@/lib/nav/region-scope';
import { isoSidoName } from '@/lib/nav/geo-cookie';

export const GEO_SUGGEST_KEY = 'kd_geo_suggest_v1';
export const GEO_SUGGEST_DISMISS_MS = 7 * 24 * 60 * 60 * 1000;
export const GEO_SUGGEST_SEEN_MS = 24 * 60 * 60 * 1000;

/**
 * ISO 접미(kd_geo) → 칩 노드(시·도). 표 이름이 정확히 같으면 그것, 아니면 이름을 품은 시·도(예: 광주·전남 → 「전남광주」).
 * 못 찾으면 null(제안 없음).
 */
export function geoScopeNode(data: HeaderRegionData | null | undefined, iso: string | null | undefined): ScopeNode | null {
  const name = isoSidoName(iso);
  if (!name || !data?.tops?.length) return null;
  const exact = data.tops.find((t) => t.label === name);
  const loose = exact ?? data.tops.find((t) => t.label.includes(name));
  return loose ? findScopeNode(data, loose.code) : null;
}

/** 숨김 기록 → 지금 숨겨야 하나. 기록 없음·깨짐 = 숨기지 않음. */
export function isGeoSuggestHidden(raw: string | null | undefined, now: number): boolean {
  if (!raw) return false;
  try {
    const v = JSON.parse(raw) as { until?: unknown };
    return typeof v?.until === 'number' && Number.isFinite(v.until) && v.until > now;
  } catch { return false; }
}

/** 숨김 기록 값(dismiss = 7일 · seen = 1일). 이미 더 긴 숨김이 있으면 줄이지 않는다. */
export function geoSuggestRecord(kind: 'seen' | 'dismissed', now: number, prevRaw?: string | null): string {
  const add = kind === 'dismissed' ? GEO_SUGGEST_DISMISS_MS : GEO_SUGGEST_SEEN_MS;
  let prev = 0;
  try { const p = prevRaw ? (JSON.parse(prevRaw) as { until?: unknown }) : null; if (typeof p?.until === 'number') prev = p.until; } catch { /* 무시 */ }
  return JSON.stringify({ until: Math.max(prev, now + add), kind, ts: now });
}

/**
 * 제안할 노드(또는 null). 입력은 전부 수화 뒤 값.
 *   currentSource = currentScope().source — 'national' 일 때만(경로·?r=·기억이 지역을 주면 제안하지 않는다).
 */
export function pickGeoSuggestion(input: {
  data: HeaderRegionData | null | undefined;
  geoIso: string | null | undefined;
  memoryCode: string | null | undefined;
  currentSource: 'path' | 'param' | 'memory' | 'national';
  hiddenRaw: string | null | undefined;
  now: number;
}): ScopeNode | null {
  if (input.memoryCode) return null;
  if (input.currentSource !== 'national') return null;
  if (isGeoSuggestHidden(input.hiddenRaw, input.now)) return null;
  return geoScopeNode(input.data, input.geoIso);
}

/** 제안 문구 — 「부산 지역으로 볼까요?」(조사 고정 «지역으로» — 받침 분기 불필요). */
export function geoSuggestText(node: Pick<ScopeNode, 'label'>): string {
  return `${node.label} 지역으로 볼까요?`;
}

/**
 * nav_region1 v2 (9) — 지역 허브에 «들어온» 것인가(시·도 /apt/region/{시도} · 시·군 /apt/area/{시도}/{시군}).
 * 이 경로에서 칩이 경로로 지역을 정하면 기억(kd_nav_region_v1)도 그 지역으로 맞춘다 → 홈·/calendar 기본 필터가 같은 지역.
 * 현장 상세(/apt/{slug})는 아니다(페이지 힌트로 칩 라벨만 바꾼다 — 기억은 사용자가 고른 지역을 지킨다).
 */
export function isRegionHubPath(pathname: string | null | undefined): boolean {
  let p = String(pathname ?? '').split(/[?#]/)[0];
  try { p = decodeURIComponent(p); } catch { /* 원문 */ }
  // 경로 → 지역 판정 자체는 matchHeaderRegion(허브 경로 표)이 한다. 여기서는 «허브 계열 경로인가» 만 본다.
  return /^\/apt\/(?:region|area)\/[^/]/.test(p);
}

/* ─── 숨김 기록 저장(localStorage) — 수화 뒤에만 부른다(region-scope 의 기억 읽기·쓰기와 같은 자리 규칙: 저장 접근은 lib 에만) ─── */

export function readGeoSuggestRaw(): string | null {
  try { return typeof window === 'undefined' ? null : window.localStorage.getItem(GEO_SUGGEST_KEY); } catch { return null; }
}

export function writeGeoSuggestRaw(v: string): void {
  try { if (typeof window !== 'undefined') window.localStorage.setItem(GEO_SUGGEST_KEY, v); } catch { /* 저장 불가 환경 — 무시 */ }
}
